import { Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { ProductNotification } from 'src/app/modules/pos/quotes/entities/products.class';
import {
  Catalog, ProductAdditionalInfo, ProductWarehouse, ProductCategory, ProductDetails, ProductsList,
  ProductWarrantyInfo, ProductSpecifications, ProductSpecificationsImage, ProductTemplateValues
} from '../../entities/product-entity';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private productsApi = {
    categories: '/api/tools/categories/',
    featuredProducts: '/api/tools/featured-products/',
    marketingAssets: '/api/tools/marketing-assets',
    productsList: '/api/tools/categories/',
    productDetails: '/api/tools/product/',
    foundProducts: '/api/tools/search',
    productAdditionalInfo: '/api/tools/product/additional-info/',
    productAvailability: '/api/tools/product/availability/',
    productWarranty: '/api/tools/product/warranty/',
    productSpecifications: '/api/tools/product/specificationDetails/',
    productSpecificationsImage: '/api/tools/product/specificationImage/',
    productNotifications: '/api/tools/notifications/',
    limitedMarketingNotification: '/api/tools/limited-marketing-notification/',
    productTemplateValues: '/api/product/templatevalues'
  };

  readonly catalogs: Catalog[] = [
    { catalogCode: 'IUS', name: 'Snap-on', logo: '/assets/images/snapon_logo.svg' },
    { catalogCode: 'IBE', name: 'Williams', logo: '/assets/images/williams_logo.png' },
    { catalogCode: 'SIO', name: 'Sioux Tools', logo: '/assets/images/sioux_logo.png' }
  ];

  private cachedCatalogCode: string | null = null;
  private cachedCategories: ProductCategory[] | null = null;
  private cachedProductsList: { [key: string]: any[] } = {};
  private cachedTemplateValues: ProductTemplateValues | null = null;
  noImage = '/assets/images/imageFallback.jpg';
  defaultPageSize = 12;
  pageSizeOptions = [12, 24, 50, 100];
  constructor(
    private apiService: ApiService,
    private appStateService: AppStateService
  ) {
    if (this.appStateService.state.isElectron) {
      this.setNoImagePathForElectron();
    }
  }

  getCategories(catalogCode: string, callbackParam: (...args: any[]) => any) {
    if (this.cachedCatalogCode === catalogCode && this.cachedCategories) {
      callbackParam(this.cachedCategories);
    } else {
      this.clearCache();
      this.cachedCatalogCode = catalogCode;
      this.apiService.get<ProductCategory[]>(this.productsApi.categories + catalogCode)
        .subscribe((value: ProductCategory[]) => {
          this.lowercaseNames(value);
          this.cachedCategories = value;
          callbackParam(value);
        });
    }
  }

  // TODO uncomment when featured products will be implemented
  // getFeaturedProducts(catalogCode: string, callbackParam: (...args: any[]) => any) {
  //   if (this.cachedFeaturedProducts) {
  //     callbackParam(this.cachedFeaturedProducts);
  //     this.lowercaseNames(this.cachedFeaturedProducts);
  //   } else {
  //     const url = this.productsApi.featuredProducts + catalogCode;
  //     this.apiService.get<FeaturedProduct[]>(url)
  //       .subscribe((value) => {
  //         this.lowercaseNames(value);
  //         this.cachedFeaturedProducts = value;
  //         callbackParam(value);
  //       });
  //   }
  // }

  getProducts(catalogCode: string, categoryId: number, callbackParam: (...args: any[]) => any) {
    const cacheKey = `${catalogCode}_${categoryId}`;
    if (this.cachedProductsList[cacheKey]) {
      callbackParam(this.cachedProductsList[cacheKey]);
    } else {
      this.apiService.get<ProductsList[]>(this.productsApi.productsList + catalogCode + '/' + categoryId)
        .subscribe((value: ProductsList) => {
          this.lowercaseNames(value.products);
          this.cachedProductsList[cacheKey] = value.products;
          callbackParam(value.products);
        });
    }
  }

  getFoundProducts(searchTerm: string, page?: number, pageSize?: number) {
    const payload = {
      name: searchTerm,
      page: page ? page : 0,
      pageSize: pageSize ? pageSize : this.defaultPageSize,
    };
    return this.apiService.post<ProductsList[]>(this.productsApi.foundProducts, payload);
  }

  getProductsDetails(catalogCode: string, productNumber: string) {
    return this.apiService.get<ProductDetails>(
      this.productsApi.productDetails + catalogCode + '/' + productNumber);
  }

  getProductAdditionalInfo(catalogCode: string, productNumber: string) {
    return this.apiService.get<ProductAdditionalInfo>(
      this.productsApi.productAdditionalInfo + catalogCode + '/' + productNumber);
  }

  getProductAvailability(productNumber: string) {
    return this.apiService.get<ProductWarehouse[]>(this.productsApi.productAvailability + productNumber);
  }

  getProductWarranty(catalogCode: string, warrantyCode: string) {
    return this.apiService.get<ProductWarrantyInfo>(this.productsApi.productWarranty + catalogCode + '/' + warrantyCode);
  }

  /**
   * Get information from API
   * @param productNumber - Product unique number
   * @returns - Product specifications object
   */
  getProductSpecifications(catalogCode: string, productNumber: string) {
    return this.apiService.get<ProductSpecifications[]>(
      this.productsApi.productSpecifications + catalogCode + '/' + productNumber);
  }

  /**
   * Get image url from API
   * @param productNumber - Product unique number
   * @returns - Product image url object
   */
  getProductSpecificationsImage(catalogCode: string, productNumber: string) {
    const url = this.productsApi.productSpecificationsImage + catalogCode + '/' + productNumber;
    return this.apiService.get<ProductSpecificationsImage[]>(url);
  }

  /**
   * Gets the notifications validated before opening a product PDP
   * @param productNumber - Product unique number
   */
  getProductNotifications(productNumber: string): Observable<ProductNotification[]> {
    return this.apiService.get<ProductNotification[]>(
      this.productsApi.productNotifications + encodeURIComponent(productNumber));
  }

  /**
   * Gets the limited marketing notifications of a product for a customer
   * @param productNumber - Product unique number
   * @param customerId - Selected customer or lead id
   * @param isLead - Whether customerId is a lead id
   * @returns - The notifications, empty when there are none
   */
  getLimitedMarketingNotifications(productNumber: string, customerId: string, isLead: boolean): Observable<ProductNotification[]> {
    return this.apiService.get<ProductNotification | ProductNotification[]>(
      this.productsApi.limitedMarketingNotification +
      encodeURIComponent(productNumber) + '/'
      + encodeURIComponent(customerId) + '/'
      + isLead)
      // Normalizes an empty body, a single notification or an array
      .pipe(map(response => [response ?? []].flat()));
  }

  /**
   * Gets the product template values (notification message types and codes), cached after the first call
   */
  getTemplateValues(): Observable<ProductTemplateValues> {
    if (this.cachedTemplateValues) {
      return of(this.cachedTemplateValues);
    }
    return this.apiService.get<ProductTemplateValues>(this.productsApi.productTemplateValues)
      .pipe(tap(templateValues => this.cachedTemplateValues = templateValues));
  }

  private clearCache() {
    this.cachedCatalogCode = null;
    this.cachedCategories = null;
    this.cachedProductsList = {};
  }

  private lowercaseNames(list: any) {
    list.forEach(element => {
      element.name = element.name.toLowerCase();
      if (element.subcategories) {
        this.lowercaseNames(element.subcategories);
      }
    });
  }

  /**
   * Sets the path for noImage picture if the app is used in electron
   */
  private setNoImagePathForElectron() {
    this.noImage = '/Program%20Files/Snapon%20IMPACT/snapon-impact-win32-x64/resources/app/assets/images/imageFallback.jpg';
  }

}
